import React, { useState, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useInviteAuth } from './InviteAuthContext';
import { X } from 'lucide-react';
import API_BASE_URL, { GOOGLE_CLIENT_ID } from '../../config';
import './InviteDashboard.css'; // Shared CSS containing styling for auth forms

export const InviteRegister: React.FC = () => {
    const { inviteRegister, inviteGoogleLogin, error } = useInviteAuth();
    const history = useHistory();
    
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [phone, setPhone] = useState('');
    const [loading, setLoading] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);

    useEffect(() => {
        const sendRemoteLog = (message: string, level: string = 'info') => {
            fetch(`${API_BASE_URL}/api/invite/auth/log`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ message, level })
            }).catch(() => {});
        };

        const initializeAndRenderGoogle = () => {
            const google = (window as any).google;
            if (google) {
                console.log('[InviteRegister] Initializing Google with Client ID:', GOOGLE_CLIENT_ID);
                sendRemoteLog(`[InviteRegister] Initializing Google with Client ID: ${GOOGLE_CLIENT_ID}`);
                google.accounts.id.initialize({
                    client_id: GOOGLE_CLIENT_ID,
                    callback: handleGoogleResponse,
                });
                const btnContainer = document.getElementById('google-signin-btn');
                if (btnContainer) {
                    google.accounts.id.renderButton(
                        btnContainer,
                        { 
                            theme: 'outline', 
                            size: 'large', 
                            width: '100%',
                            text: 'signup_with' 
                        }
                    );
                }
            } else {
                console.warn('[InviteRegister] Google object not found on window');
                sendRemoteLog(`[InviteRegister] Google object not found on window`, 'warn');
            }
        };

        sendRemoteLog(`[InviteRegister] Mounting component. Client ID configured: ${GOOGLE_CLIENT_ID}`);

        let script = document.querySelector('script[src="https://accounts.google.com/gsi/client"]') as HTMLScriptElement;
        
        const handleScriptLoad = () => {
            sendRemoteLog(`[InviteRegister] Google library script loaded successfully`);
            initializeAndRenderGoogle();
        };

        if (script) {
            const google = (window as any).google;
            if (google) {
                initializeAndRenderGoogle();
            } else {
                script.addEventListener('load', handleScriptLoad);
            }
        } else {
            script = document.createElement('script');
            script.src = 'https://accounts.google.com/gsi/client';
            script.async = true;
            script.defer = true;
            document.body.appendChild(script);
            script.addEventListener('load', handleScriptLoad);
        }

        return () => {
            if (script) {
                script.removeEventListener('load', handleScriptLoad);
            }
        };
    }, []);

    const handleGoogleResponse = async (response: any) => {
        setLoading(true);
        setFormError(null);
        try {
            await inviteGoogleLogin(response.credential);
            history.push('/invite/dashboard');
        } catch (err: any) {
            setFormError(err.message || 'Google Login failed');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setFormError(null);

        if (!name || !email || !password) {
            setFormError('Please fill in all required fields');
            return;
        }

        setLoading(true);
        try {
            await inviteRegister(name, email, password, phone || undefined);
            history.push('/invite/dashboard');
        } catch (err: any) {
            setFormError(err.message || 'Registration failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="invite-auth-container">
            <div className="invite-auth-card">
                <Link to="/" className="invite-auth-close" aria-label="Close page">
                    <X size={18} />
                </Link>
                <div className="auth-header">
                    <h2>✉️ Shadigram Invite Hub</h2>
                    <p>Create a standalone account to send free invitations</p>
                </div>

                <form onSubmit={handleSubmit} className="auth-form">
                    {(formError || error) && (
                        <div className="auth-error-badge">
                            ⚠️ {formError || error}
                        </div>
                    )}

                    <div className="form-group">
                        <label htmlFor="name">Full Name *</label>
                        <input
                            type="text"
                            id="name"
                            placeholder="e.g. Nitin Sharma"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="email">Email Address *</label>
                        <input
                            type="email"
                            id="email"
                            placeholder="e.g. nitin@example.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="phone">Phone Number (Optional)</label>
                        <input
                            type="tel"
                            id="phone"
                            placeholder="e.g. +919876543210"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                        />
                        <small className="form-hint">Used for sending test invites to yourself</small>
                    </div>

                    <div className="form-group">
                        <label htmlFor="password">Password *</label>
                        <input
                            type="password"
                            id="password"
                            placeholder="Minimum 6 characters"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                        />
                    </div>

                    <button type="submit" className="auth-submit-btn" disabled={loading}>
                        {loading ? 'Creating Account...' : 'Sign Up — It\'s Free ✨'}
                    </button>

                    <div style={{ display: 'flex', alignItems: 'center', margin: '1.5rem 0', color: 'var(--invite-text-light)' }}>
                        <hr style={{ flex: 1, border: 'none', borderTop: '1px solid rgba(0,0,0,0.1)' }} />
                        <span style={{ padding: '0 10px', fontSize: '0.85rem' }}>or</span>
                        <hr style={{ flex: 1, border: 'none', borderTop: '1px solid rgba(0,0,0,0.1)' }} />
                    </div>

                    <div id="google-signin-btn" style={{ width: '100%' }}></div>
                </form>

                <div className="auth-footer">
                    <p>Already have an account? <Link to="/invite/login">Log In</Link></p>
                </div>
            </div>
        </div>
    );
};
export default InviteRegister;
